/**
 * VerificationPanel — UI surface for the lifecycle verification engine.
 * See docs/COMPLIANCE_LIFECYCLE_PLAN.md §6.
 *
 * Renders the result of any of the 6 verification stages with a traffic-light
 * header, blocker/warning/info buckets, deep-link node ids, and fix hints.
 *
 * Used by:
 *   - ComplianceStructureBuilder (Stages 1, 2)
 *   - Frameworks page (Stage 3 before publish-and-activate, Stage 4 before assign)
 *   - Company dashboard (Stages 5, 6 — Phase 2 skeletons until wired)
 */

import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info } from 'lucide-react';

export type VerificationSeverity = 'blocker' | 'warning' | 'info';

export interface VerificationIssue {
  code: string;
  severity: VerificationSeverity;
  message: string;
  nodeId?: number;
  field?: string;
  fixHint?: string;
}

export interface VerificationResult {
  stage: 1 | 2 | 3 | 4 | 5 | 6;
  status: 'pass' | 'warn' | 'fail';
  blockers: VerificationIssue[];
  warnings: VerificationIssue[];
  info: VerificationIssue[];
  canProceed: boolean;
}

interface Props {
  result: VerificationResult | null;
  loading?: boolean;
  title?: string;
  onClose?: () => void;
  onIssueClick?: (issue: VerificationIssue) => void;
}

const STAGE_LABELS: Record<number, string> = {
  1: 'Pre-publish (template integrity)',
  2: 'Pre-mapping (clause integrity)',
  3: 'Pre-activate (version integrity)',
  4: 'Pre-assignment (company readiness)',
  5: 'Pre-audit (audit readiness)',
  6: 'Pre-certify (certificate gating)',
};

const SEVERITY_STYLES: Record<VerificationSeverity, { bg: string; border: string; text: string; Icon: any }> = {
  blocker: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', Icon: XCircle },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', Icon: AlertTriangle },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', Icon: Info },
};

const STATUS_HEADER: Record<'pass' | 'warn' | 'fail', { bg: string; text: string; label: string; Icon: any }> = {
  pass: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'PASS', Icon: CheckCircle2 },
  warn: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'WARNINGS', Icon: AlertTriangle },
  fail: { bg: 'bg-red-100', text: 'text-red-800', label: 'BLOCKED', Icon: XCircle },
};

export const VerificationPanel: React.FC<Props> = ({ result, loading, title, onClose, onIssueClick }) => {
  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <p className="text-xs text-slate-500">Running verification…</p>
      </div>
    );
  }
  if (!result) return null;

  const header = STATUS_HEADER[result.status];
  const HeaderIcon = header.Icon;
  const allIssues = [...result.blockers, ...result.warnings, ...result.info];

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-w-md">
      <div className={`flex items-center justify-between px-4 py-3 ${header.bg}`}>
        <div className="flex items-center gap-2">
          <HeaderIcon size={18} className={header.text} />
          <div>
            <p className={`text-xs font-bold ${header.text}`}>
              Stage {result.stage} — {header.label}
            </p>
            <p className="text-[11px] text-slate-600">{title ?? STAGE_LABELS[result.stage]}</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-lg leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        )}
      </div>

      <div className="p-3">
        <div className="flex gap-3 mb-3 text-[11px]">
          <span className="font-semibold text-red-700">{result.blockers.length} blocker(s)</span>
          <span className="font-semibold text-amber-700">{result.warnings.length} warning(s)</span>
          <span className="font-semibold text-blue-700">{result.info.length} info</span>
        </div>

        {allIssues.length === 0 ? (
          <p className="text-xs text-emerald-700 font-medium">All checks passed. Ready to proceed.</p>
        ) : (
          <ul className="space-y-2 max-h-80 overflow-y-auto">
            {allIssues.map((issue, idx) => {
              const style = SEVERITY_STYLES[issue.severity];
              const StyleIcon = style.Icon;
              return (
                <li
                  key={`${issue.code}-${idx}`}
                  className={`${style.bg} ${style.border} border rounded-md p-2 transition-all ${onIssueClick && issue.nodeId ? 'cursor-pointer hover:shadow-md hover:ring-2 hover:ring-indigo-300' : ''}`}
                  onClick={() => onIssueClick && issue.nodeId && onIssueClick(issue)}
                  title={onIssueClick && issue.nodeId ? 'Click to jump to this node in the Builder' : undefined}
                >
                  <div className="flex items-start gap-2">
                    <StyleIcon size={14} className={`${style.text} mt-0.5 flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] font-mono font-bold ${style.text}`}>{issue.code}</p>
                      <p className={`text-xs ${style.text}`}>{issue.message}</p>
                      {issue.fixHint && (
                        <p className="text-[11px] text-slate-600 mt-1 italic">→ {issue.fixHint}</p>
                      )}
                      {issue.nodeId && onIssueClick && (
                        <p className="text-[10px] text-indigo-600 font-semibold mt-1">Click to jump to this node →</p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Tiny client API helpers — used by the buttons that call into the engine
// ─────────────────────────────────────────────────────────────────────────────

const getCsrfToken = () =>
  document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

export const verificationApi = {
  authHeaders(): HeadersInit {
    return {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    };
  },

  async prePublish(templateId: number): Promise<VerificationResult> {
    const r = await fetch(`/api/compliance/definition/verify/templates/${templateId}/pre-publish`, {
      headers: this.authHeaders(),
    });
    const j = await r.json();
    return j.data;
  },

  async preMapping(templateId: number, previousVersionId?: number): Promise<VerificationResult> {
    const qs = previousVersionId !== undefined ? `?previousVersionId=${previousVersionId}` : '';
    const r = await fetch(`/api/compliance/definition/verify/templates/${templateId}/pre-mapping${qs}`, {
      headers: this.authHeaders(),
    });
    const j = await r.json();
    return j.data;
  },

  async preActivate(versionId: number): Promise<VerificationResult> {
    const r = await fetch(`/api/compliance/definition/verify/framework-versions/${versionId}/pre-activate`, {
      headers: this.authHeaders(),
    });
    const j = await r.json();
    return j.data;
  },

  async preAssignment(versionId: number, companyId: number): Promise<VerificationResult> {
    const r = await fetch(
      `/api/compliance/definition/verify/framework-versions/${versionId}/pre-assignment/${companyId}`,
      { headers: this.authHeaders() },
    );
    const j = await r.json();
    return j.data;
  },
};

export const lifecycleAuthorApi = {
  authHeaders(): HeadersInit {
    return {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    };
  },

  async publishAndActivate(templateId: number, frameworkVersionId: number): Promise<{ ok: boolean; data?: any; error?: string; verification?: VerificationResult }> {
    const r = await fetch('/api/compliance/definition/lifecycle/publish-and-activate', {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ templateId, frameworkVersionId }),
    });
    const j = await r.json();
    if (!j.success) {
      return { ok: false, error: j.message, verification: j.verification };
    }
    return { ok: true, data: j.data };
  },

  async cloneVersion(sourceVersionId: number, newVersion: string): Promise<{ ok: boolean; data?: any; error?: string }> {
    const r = await fetch('/api/compliance/definition/lifecycle/clone-version', {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ sourceVersionId, newVersion }),
    });
    const j = await r.json();
    if (!j.success) return { ok: false, error: j.message };
    return { ok: true, data: j.data };
  },

  async outdatedCompanies(): Promise<any[]> {
    const r = await fetch('/api/compliance/definition/lifecycle/outdated-companies', {
      headers: this.authHeaders(),
    });
    const j = await r.json();
    return j.data ?? [];
  },

  async getLineage(assignmentId: number): Promise<any[]> {
    const r = await fetch(`/api/compliance/definition/lifecycle/assignments/${assignmentId}/lineage`, {
      headers: this.authHeaders(),
    });
    const j = await r.json();
    return j.data ?? [];
  },
};

export const auditPeriodApi = {
  authHeaders(): HeadersInit {
    return {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    };
  },

  async open(frameworkAssignmentId: number, auditorId?: number, scopeNotes?: string) {
    const r = await fetch('/api/compliance/audit-periods/open', {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ frameworkAssignmentId, auditorId, scopeNotes }),
    });
    return r.json();
  },

  async cancel(id: number, reason: string) {
    const r = await fetch(`/api/compliance/audit-periods/${id}/cancel`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ reason }),
    });
    return r.json();
  },

  async listForCompany() {
    const r = await fetch('/api/compliance/audit-periods/company', { headers: this.authHeaders() });
    const j = await r.json();
    return j.data ?? [];
  },
};
